// Influence tracing.
//
// Given one turn of a captured AI-agent session, trace the web of
// influences that led into it — and let a *fuzziness* control decide how
// loosely the agent is allowed to pivot across artifact types. Tight =
// same-session prompt lineage (provenance you can defend). Loose = bridge
// across files, commands, hosts, sessions, and endpoints (inference, not
// provenance). The looser it gets, the more this is a hypothesis about
// influence rather than a single hard cause — which is exactly why the
// output carries per-link confidence instead of asserting one origin.
//
// Runs entirely in the browser on the analytics + LLM-proxy surfaces the
// dashboard already uses. Two data substrates, for two questions:
//   • get_session_prompts — the chronological transcript of one session.
//     Reads WHAT happened in order (the analytics API has no prompt-text
//     field, so this is the only way to read content). Reuses the
//     dashboard's getTraces path, so tenant auth + scoping come free.
//   • analytics_query — the graph walk ACROSS sessions / endpoints /
//     files / tools / time. Finds WHERE an artifact first appeared so an
//     influence can reach back into an earlier session.
//
// Each cited artifact carries an openable id so the view can pop it into
// the trace column — the operator follows the trail by jumping between
// the traces it references.

import { Type } from "@earendil-works/pi-ai";
import { runSubAgent } from "./subAgent.js";
import {
    createAnalyticsQueryTool,
    createExpandPreviewTool,
    analyticsSchemaTool,
} from "./analyticsTool.js";
import { query } from "./dataClient.js";

// ── fuzziness presets ───────────────────────────────────────────────
// Each tunes how far the agent may pivot across artifact types and how
// it should label confidence. The id rides in the report so the view can
// caption "how loose did we let this get".
export const FUZZINESS_LEVELS = [
    {
        id: "tight",
        label: "Tight",
        note: "Same-session prompt lineage only — provenance, not inference.",
        guidance:
            "STAY TIGHT. Trace influence only through this session's own prompt/turn lineage and " +
            "direct, explicit references (a turn that literally cites a file/command another turn " +
            "produced). Do NOT bridge across sessions or infer influence from mere co-occurrence. " +
            "If the chain leaves this session, stop and say so. Mark confidence high only for " +
            "explicit, in-session links.",
    },
    {
        id: "balanced",
        label: "Balanced",
        note: "In-session lineage plus well-supported cross-session links.",
        guidance:
            "BE BALANCED. Prefer this session's lineage, but follow an artifact (a specific file, " +
            "command, skill, host) into an EARLIER session when the evidence is strong (same exact " +
            "artifact, plausible time order). Treat shared topics or loosely-similar text as weak " +
            "evidence, not a link. Set confidence per link by how direct the evidence is.",
    },
    {
        id: "loose",
        label: "Loose",
        note: "Cross-artifact, cross-session inference — hypotheses welcome.",
        guidance:
            "GO LOOSE. Pivot freely across artifact types and sessions/endpoints to surface the " +
            "fullest plausible web of influence — a file touched here that first appeared there, a " +
            "command pattern that recurs, a host that ties sessions together. These are HYPOTHESES: " +
            "label speculative links low confidence and say what would confirm them. Never present " +
            "an inferred bridge as established fact.",
    },
];
export const DEFAULT_FUZZINESS = "balanced";

// Persisted fuzziness preference — sticks across traces + reloads, the
// same shape as the task-model localStorage preference (chatCreds.js).
// A UI preference, not tenant data, so it's a single browser-global key
// (vs taskModel's tenant-keyed one). Validated against the known levels
// so a stale/garbage value falls back to the default.
const FUZZINESS_STORAGE_KEY = "dashboard:influenceFuzziness";

export function readFuzziness() {
    try {
        if (typeof localStorage === "undefined") return DEFAULT_FUZZINESS;
        const v = localStorage.getItem(FUZZINESS_STORAGE_KEY);
        return FUZZINESS_LEVELS.some((l) => l.id === v) ? v : DEFAULT_FUZZINESS;
    } catch {
        return DEFAULT_FUZZINESS;
    }
}

export function writeFuzziness(value) {
    try {
        if (typeof localStorage === "undefined") return;
        if (FUZZINESS_LEVELS.some((l) => l.id === value)) {
            localStorage.setItem(FUZZINESS_STORAGE_KEY, value);
        }
    } catch {
        /* ignore — preference persistence is best-effort */
    }
}

function fuzzinessGuidance(id) {
    const lvl = FUZZINESS_LEVELS.find((l) => l.id === id) || FUZZINESS_LEVELS[1];
    return lvl.guidance;
}

// ── output contract ─────────────────────────────────────────────────
// An influence trail, not a single deterministic cause. Each influence
// cites an openable artifact; the view turns trace/prompt refs into
// clickable tabs. Steered via the return_result description (not
// typebox-enforced).
export const INFLUENCE_REPORT_SCHEMA = {
    type: "object",
    properties: {
        summary: { type: "string", description: "2-5 sentences: what shaped this turn, and how far back the influence reaches. Plain prose for the reader." },
        influences: {
            type: "array",
            description: "The influence links you found, ordered most-load-bearing first. 3-10 entries. Each cites ONE artifact that influenced the turn.",
            items: {
                type: "object",
                properties: {
                    label: { type: "string", description: "Short description of this influence (what it is)." },
                    relation: { type: "string", description: "How it influenced the turn, e.g. 'originated the task', 'introduced this file', 'reused this command', 'set the approach'." },
                    confidence: { type: "string", description: "high | medium | low — how direct the evidence is. Inferred cross-artifact bridges should be low." },
                    why: { type: "string", description: "1-2 sentences of the actual evidence — cite what you saw in the tool results." },
                    ref: {
                        type: "object",
                        description: "The artifact this influence points at. trace/prompt refs are openable in the trace column.",
                        properties: {
                            kind: { type: "string", description: "trace | prompt | session | file | command | host | tool | skill | endpoint" },
                            id: { type: "string", description: "For trace/prompt: the prompt_id from get_session_prompts (openable). For session: the session_id (notification_id). For file/command/host/tool/skill: the literal value." },
                            label: { type: "string", description: "Human label for the artifact." },
                        },
                    },
                },
            },
        },
        pivot: {
            type: "object",
            description: "Optional: the single turning point where the turn's approach or direction took shape — the most decisive influence. Omit if there isn't a clear one.",
            properties: {
                label: { type: "string" },
                timestamp: { type: "string", description: "ISO 8601" },
                ref: { type: "object", properties: { kind: { type: "string" }, id: { type: "string" }, label: { type: "string" } } },
            },
        },
        fuzziness_note: { type: "string", description: "One line on how loose the cross-artifact pivots got and how much of this is inference vs. provenance — so the reader calibrates trust." },
    },
    required: ["summary", "influences"],
};

function systemPrompt(fuzzinessId) {
    return [
        "You trace INFLUENCE for an AI-observability dashboard. The operator is looking at one TURN",
        "of one captured AI-agent session and wants to understand what shaped it — the instructions,",
        "artifacts, and earlier work that influenced this turn. You are NOT asserting a single",
        "deterministic cause; you are surfacing a web of influences, each with honest confidence.",
        "",
        "Tools:",
        "  • get_session_prompts(session_id) — chronological transcript of ONE session (oldest-first,",
        "    with prompt_id + timestamp + summary). Read it to understand the in-session lineage and",
        "    find the prompts that set things in motion. The turn's own session_id is given below.",
        "  • analytics_describe_schema + analytics_query — the graph walk across sessions, endpoints,",
        "    files, tools, skills, and time. Use it to find WHERE an artifact first appeared. Call",
        "    analytics_describe_schema first if unsure of fields/edges. Use a generous time range.",
        "  • expand_preview — page a large analytics result you already ran.",
        "",
        fuzzinessGuidance(fuzzinessId),
        "",
        "Method: read the turn's session transcript first to anchor the in-session lineage; then,",
        "as the fuzziness allows, walk the graph backward through the turn's salient artifacts. Cite",
        "ONLY artifacts that actually appear in tool results — never invent ids or text. For each",
        "influence give an openable ref: use the prompt_id from get_session_prompts for trace/prompt",
        "refs. When done, call return_result with the influence report — do not narrate in prose.",
    ].join("\n");
}

// ── turn → task ─────────────────────────────────────────────────────

function safeJson(v, cap = 600) {
    let s;
    try { s = typeof v === "string" ? v : JSON.stringify(v); }
    catch { s = String(v); }
    if (s == null) return "";
    return s.length > cap ? `${s.slice(0, cap)}…` : s;
}

function toolResultText(content) {
    if (typeof content === "string") return content;
    if (Array.isArray(content)) return content.map((c) => (c?.type === "text" ? c.text : safeJson(c))).join("\n");
    return safeJson(content);
}

function turnContentText(turn) {
    const parts = [];
    for (const b of turn?.blocks || []) {
        if (b?.type === "text" && b.text) parts.push(b.text);
        else if (b?.type === "thinking" && b.thinking) parts.push(`[thinking] ${b.thinking}`);
        else if (b?.type === "tool_use") parts.push(`[tool_use ${b.name || "?"}] ${safeJson(b.input)}`);
        else if (b?.type === "tool_result") parts.push(`[tool_result${b.isError ? " ERROR" : ""}] ${safeJson(toolResultText(b.content))}`);
    }
    return parts.join("\n").slice(0, 4000);
}

function turnEntities(turn) {
    const out = { files: [], hosts: [], commands: [], skills: [], tools: [], urls: [] };
    const seen = new Set();
    const push = (k, v) => {
        if (!v) return;
        const key = `${k}:${v}`;
        if (seen.has(key)) return;
        seen.add(key);
        out[k].push(v);
    };
    for (const b of turn?.blocks || []) {
        const f = b?.type === "tool_use" ? b.facets : null;
        if (!f) continue;
        for (const v of f.toolCalls || []) push("tools", v);
        for (const v of f.commands || []) push("commands", v);
        for (const v of f.files || []) push("files", v);
        for (const v of f.hosts || []) push("hosts", v);
        for (const v of f.skills || []) push("skills", v);
        for (const v of f.urls || []) push("urls", v);
    }
    return out;
}

function asIso(ts) {
    if (!ts) return "";
    if (ts instanceof Date) return Number.isNaN(ts.getTime()) ? "" : ts.toISOString();
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? String(ts) : d.toISOString();
}

function buildTask(turn, trace, turnIndex) {
    const total = trace?.turns?.length || 0;
    const ent = turnEntities(turn);
    const entLine = Object.entries(ent)
        .filter(([, v]) => v.length)
        .map(([k, v]) => `${k}=[${v.slice(0, 12).join(", ")}]`)
        .join("  ") || "(none extracted)";
    return [
        "Trace what influenced this turn.",
        "",
        "TURN (the thing to explain):",
        `- trace_id: ${trace?.id || ""}`,
        `- session_id (notification_id, this turn's session): ${trace?.sessionId || ""}`,
        `- endpoint: ${trace?.endpointName || ""} (id ${trace?.endpointId || ""})`,
        `- timestamp: ${asIso(trace?.timestamp)} — treat this as "now"; influences are BEFORE this`,
        `- model / provider: ${trace?.model || "?"} / ${trace?.provider || "?"}`,
        `- role: ${turn?.role || "?"} (turn ${turnIndex + 1} of ${total})`,
        `- salient artifacts: ${entLine}`,
        `- session summary: ${trace?.summary || ""}`,
        "",
        "TURN CONTENT:",
        turnContentText(turn) || "(no readable content)",
        "",
        "Start with get_session_prompts on this turn's session, then walk the graph as the fuzziness",
        "allows. Return the influence report.",
    ].join("\n");
}

// ── session transcript tool ─────────────────────────────────────────
export function createSessionPromptsTool() {
    return {
        name: "get_session_prompts",
        label: "Session prompts",
        description:
            "Fetch the chronological transcript of ONE session: every prompt/turn summary, " +
            "oldest-first, with prompt_id + timestamp. Use it to read a session's story in order " +
            "and find the prompts that influenced later turns. Pass session_id (the notification_id). " +
            "Returns { session_id, count, prompts: [{ prompt_id, timestamp, summary, model, provider, cluster }] }. " +
            "The prompt_id values are openable trace ids — cite them as trace/prompt refs.",
        parameters: Type.Object({
            session_id: Type.String({ description: "The session id (notification_id) to fetch the prompt timeline for." }),
        }),
        async execute(_toolCallId, { session_id }) {
            const rows = await query("getTraces", {
                scopePath: [{ dim: "sessions", id: session_id }],
                sort: "oldest",
                limit: 500,
                range: null,
            });
            const prompts = (Array.isArray(rows) ? rows : []).map((t) => ({
                prompt_id: t.id,
                timestamp: asIso(t.timestamp),
                summary: t.summary || "",
                model: t.model || "",
                provider: t.provider || "",
                cluster: t.primaryClusterName || t.clusterName || "",
            }));
            const payload = { session_id, count: prompts.length, prompts };
            return { content: [{ type: "text", text: JSON.stringify(payload) }], details: payload };
        },
    };
}

// ── runner ──────────────────────────────────────────────────────────

// Run the influence-tracing agent for one turn at a given fuzziness.
// Resolves { status: "done", report } | { status: "aborted" } |
// { status: "error", error, lastText }. onEvent streams pi-agent
// lifecycle events (tool_execution_start/end, etc.) for a live log.
export async function runInfluenceTrace({
    trace,
    turn,
    turnIndex,
    fuzziness = DEFAULT_FUZZINESS,
    model = null,
    thinkingLevel = null,
    signal,
    onEvent,
}) {
    if (!trace || !turn) return { status: "error", error: new Error("missing trace or turn") };
    const nsKey = `influence:${trace.id}:${turnIndex}:${fuzziness}`;
    const getNamespaceKey = () => nsKey;
    const tools = [
        analyticsSchemaTool,
        createAnalyticsQueryTool({ getNamespaceKey }),
        createExpandPreviewTool({ getNamespaceKey }),
        createSessionPromptsTool(),
    ];

    // runSubAgent converts most failures into { status:"error" }, but a
    // synchronous throw during runtime/tool construction (or in buildTask)
    // would reject. Catch it here so the runner ALWAYS resolves to a
    // status object — never a rejected promise the caller has to guard.
    let r;
    try {
        r = await runSubAgent({
            role: "primary",
            model,
            thinkingLevel,
            signal,
            onEvent,
            tools,
            outputSchema: INFLUENCE_REPORT_SCHEMA,
            systemPrompt: systemPrompt(fuzziness),
            task: buildTask(turn, trace, turnIndex),
        });
    } catch (e) {
        return { status: "error", error: e instanceof Error ? e : new Error(String(e)) };
    }

    if (r.status === "done" && r.result && typeof r.result === "object") {
        return { status: "done", report: r.result, messages: r.messages };
    }
    if (r.status === "aborted") return { status: "aborted" };
    return {
        status: "error",
        error: r.error || new Error(r.reason || "the agent returned no report"),
        lastText: r.lastAssistantText || null,
    };
}
